// Mirrors backend/src/main/java/com/kervolt/attendance/services/auth/PasswordPolicy.java —
// keep thresholds and reason wording in sync so frontend hints match the backend 400.

const MIN = 8;
const MAX = 64;
const SEQ = 5; // sequential-run length that trips the rule
const KEYBOARD_ROWS = ['qwertyuiop', 'asdfghjkl', 'zxcvbnm', '1234567890'];

// Trimmed inline blocklist — top common entries from
// backend/src/main/resources/security/common-passwords.txt
const COMMON = new Set<string>([
    'password', '123456', '12345678', '123456789', '12345', '1234567', '1234567890',
    'qwerty', 'qwertyuiop', 'abc123', 'password1', 'password123', '111111', '123123',
    '000000', 'iloveyou', 'admin', 'administrator', 'welcome', 'welcome1', 'login',
    'passw0rd', 'letmein', 'monkey', 'dragon', 'sunshine', 'princess', 'football',
    'baseball', 'master', 'shadow', 'superman', 'batman', 'trustno1', 'whatever',
    'qazwsx', 'zxcvbn', 'asdfgh', 'asdfghjkl', '1q2w3e4r', '1qaz2wsx', 'qwe123',
    'test', 'test123', 'root', 'toor', 'changeme', 'secret', 'default', 'user',
]);

const charClasses = (pw: string): number => {
    let lower = false;
    let upper = false;
    let digit = false;
    let symbol = false;
    for (const c of pw) {
        if (/[a-z]/.test(c)) lower = true;
        else if (/[A-Z]/.test(c)) upper = true;
        else if (/[0-9]/.test(c)) digit = true;
        else symbol = true;
    }
    return (lower ? 1 : 0) + (upper ? 1 : 0) + (digit ? 1 : 0) + (symbol ? 1 : 0);
};

const matchesUsername = (pw: string, username?: string): boolean => {
    if (username === undefined || username === null || username.trim().length < 3) return false;
    const p = pw.toLowerCase();
    const u = username.trim().toLowerCase();

    if (p === u) return true;
    const stripped = p.replace(/\d+$/, '');
    if (stripped === u) return true;

    if (u.length < 4) return false;
    if (p.includes(u)) return true;
    const reversed = u.split('').reverse().join('');
    return p.includes(reversed);
};

const matchesPhone = (pw: string, phone?: string): boolean => {
    if (phone === undefined || phone === null) return false;
    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length < 6) return false;
    const pwDigits = pw.replace(/\D/g, '');
    return pwDigits.includes(phoneDigits);
};

const isCommon = (pw: string): boolean => {
    const p = pw.toLowerCase();
    if (COMMON.has(p)) return true;
    const base = p.replace(/\d+$/, '');
    return base !== '' && COMMON.has(base);
};

const isAllSameChar = (pw: string): boolean => {
    return pw.length >= 2 && new Set(pw.split('')).size === 1;
};

const hasSequentialRun = (pw: string): boolean => {
    const p = pw.toLowerCase();
    // straight ascending/descending over char codes (covers abcde and 12345)
    let run = 1;
    for (let i = 1; i < p.length; i++) {
        const d = p.charCodeAt(i) - p.charCodeAt(i - 1);
        run = d === 1 || d === -1 ? run + 1 : 1;
        if (run >= SEQ) return true;
    }
    // keyboard rows (forward and reverse)
    for (const row of KEYBOARD_ROWS) {
        const rev = row.split('').reverse().join('');
        for (let i = 0; i + SEQ <= row.length; i++) {
            if (p.includes(row.substring(i, i + SEQ)) || p.includes(rev.substring(i, i + SEQ))) return true;
        }
    }
    return false;
};

const isRepeatedUnit = (pw: string): boolean => {
    if (pw.length < 4) return false;
    for (let unit = 1; unit <= 3; unit++) {
        if (pw.length % unit !== 0) continue;
        const u = pw.substring(0, unit);
        if (u.repeat(pw.length / unit) === pw) return true;
    }
    return false;
};

/** Translation keys (namespace `auth`) for each rule a password can break. */
export type PasswordIssueKey =
    | 'passwordPolicy.tooShort'
    | 'passwordPolicy.tooLong'
    | 'passwordPolicy.charTypes'
    | 'passwordPolicy.basedOnUsername'
    | 'passwordPolicy.containsPhone'
    | 'passwordPolicy.tooCommon'
    | 'passwordPolicy.singleChar'
    | 'passwordPolicy.sequence'
    | 'passwordPolicy.repeatedPattern';

export interface PasswordIssue {
    key: PasswordIssueKey;
    values?: { min?: number; max?: number };
}

/**
 * The rules a password breaks, as `auth` translation keys, for a translated hint:
 * `passwordIssues(pw).map(i => t(i.key, i.values))` with `useTranslation('auth')`.
 */
export const passwordIssues = (password: string, username?: string, phone?: string): PasswordIssue[] => {
    const issues: PasswordIssue[] = [];
    const pw = password ?? '';

    if (pw.length < MIN) issues.push({ key: 'passwordPolicy.tooShort', values: { min: MIN } });
    if (pw.length > MAX) issues.push({ key: 'passwordPolicy.tooLong', values: { max: MAX } });
    if (charClasses(pw) < 3) issues.push({ key: 'passwordPolicy.charTypes' });
    if (matchesUsername(pw, username)) issues.push({ key: 'passwordPolicy.basedOnUsername' });
    if (matchesPhone(pw, phone)) issues.push({ key: 'passwordPolicy.containsPhone' });
    if (isCommon(pw)) issues.push({ key: 'passwordPolicy.tooCommon' });
    if (isAllSameChar(pw)) issues.push({ key: 'passwordPolicy.singleChar' });
    if (hasSequentialRun(pw)) issues.push({ key: 'passwordPolicy.sequence' });
    if (isRepeatedUnit(pw)) issues.push({ key: 'passwordPolicy.repeatedPattern' });

    return issues;
};

const ENGLISH: Record<PasswordIssueKey, string> = {
    'passwordPolicy.tooShort': `must be at least ${MIN} characters`,
    'passwordPolicy.tooLong': `must be at most ${MAX} characters`,
    'passwordPolicy.charTypes': 'must mix at least 3 character types (lowercase, uppercase, digit, symbol)',
    'passwordPolicy.basedOnUsername': 'must not be based on your username',
    'passwordPolicy.containsPhone': 'must not contain your phone number',
    'passwordPolicy.tooCommon': 'is too common',
    'passwordPolicy.singleChar': 'must not repeat a single character',
    'passwordPolicy.sequence': 'must not contain a sequence like abcde or 12345',
    'passwordPolicy.repeatedPattern': 'must not repeat a short pattern',
};

/** English reasons, worded like the backend's 400. For a translated hint use `passwordIssues`. */
export const validatePassword = (password: string, username?: string, phone?: string): string[] =>
    passwordIssues(password, username, phone).map(issue => ENGLISH[issue.key]);
